// Creator View Tracking Utilities
import { supabase } from '../lib/supabase.js';

/**
 * Track when a creator profile is viewed
 * Call this when someone visits a creator's profile page
 * 
 * @param {string} identifier - Can be wallet address, handle, or ID
 * @param {string} identifierType - 'wallet', 'handle', or 'id'
 * @returns {Promise<number|null>} - New view count or null if failed
 */
export const trackCreatorView = async (identifier, identifierType = 'wallet') => {
  try {
    console.log(`👀 Tracking creator view: ${identifierType} = ${identifier}`);
    
    let functionName;
    let paramName;
    
    switch (identifierType) {
      case 'wallet':
        functionName = 'increment_creator_views';
        paramName = 'p_creator_wallet';
        break;
      case 'handle':
        functionName = 'increment_creator_views_by_handle';
        paramName = 'p_handle';
        break;
      case 'id':
        functionName = 'increment_creator_views_by_id';
        paramName = 'p_creator_id';
        break;
      default:
        throw new Error(`Invalid identifier type: ${identifierType}`);
    }
    
    const { data, error } = await supabase
      .rpc(functionName, {
        [paramName]: identifier
      });
    
    if (error) {
      console.error('❌ Error tracking creator view:', error);
      return null;
    }
    
    console.log(`✅ Creator view tracked. New count: ${data}`);
    return data;
    
  } catch (error) {
    console.error('❌ Failed to track creator view:', error);
    return null;
  }
};

/**
 * Get most viewed creators
 * 
 * @param {number} limit - Number of creators to fetch
 * @returns {Promise<Array>} - Array of most viewed creators
 */
export const getMostViewedCreators = async (limit = 20) => {
  try {
    console.log(`📊 Fetching ${limit} most viewed creators...`);
    
    const { data, error } = await supabase
      .rpc('get_most_viewed_creators', { p_limit: limit });
    
    if (error) {
      console.error('❌ Error fetching most viewed creators:', error);
      throw error;
    }
    
    console.log('✅ Fetched most viewed creators:', data);
    return data || [];
    
  } catch (error) {
    console.error('❌ Failed to fetch most viewed creators:', error);
    return [];
  }
};

/**
 * Get trending creators (balanced by views, pieces, and volume)
 * 
 * @param {number} limit - Number of creators to fetch
 * @returns {Promise<Array>} - Array of trending creators
 */
export const getTrendingCreators = async (limit = 20) => {
  try {
    console.log(`🔥 Fetching ${limit} trending creators...`);
    
    const { data, error } = await supabase
      .rpc('get_trending_creators', { p_limit: limit });
    
    if (error) {
      console.error('❌ Error fetching trending creators:', error);
      throw error;
    }
    
    console.log('✅ Fetched trending creators:', data);
    return data || [];
    
  } catch (error) {
    console.error('❌ Failed to fetch trending creators:', error);
    return [];
  }
};

/**
 * Format creator data for display with view count
 * 
 * @param {Object} creator - Creator data from database
 * @param {number} solPrice - Current SOL price in USD
 * @returns {Object} - Formatted creator data
 */
export const formatCreatorWithViews = (creator, solPrice = 0) => {
  if (!creator) return null;
  
  const volume = parseFloat(creator.total_volume || 0);
  const volumeUSD = volume * solPrice;
  
  return {
    id: creator.id,
    name: creator.handle,
    handle: `@${creator.handle}`,
    avatar: creator.profile_picture_url || null,
    pieces: `${creator.total_pieces_created} piece${creator.total_pieces_created !== 1 ? 's' : ''}`,
    piecesCount: creator.total_pieces_created,
    volume: `${volume.toFixed(2)} SOL`,
    volumeSOL: volume,
    volumeUSD: volumeUSD,
    views: creator.total_views || 0,
    verified: creator.is_verified || false,
    wallet: creator.wallet,
    link: `/creator/${creator.handle}`,
    trendingScore: creator.trending_score || 0
  };
};

/**
 * Get formatted trending creators for UI
 * 
 * @param {number} limit - Number of creators to fetch
 * @param {number} solPrice - Current SOL price in USD
 * @returns {Promise<Array>} - Array of formatted creators
 */
export const getFormattedTrendingCreators = async (limit = 20, solPrice = 0) => {
  try {
    const creators = await getTrendingCreators(limit);
    const formatted = creators.map(creator => formatCreatorWithViews(creator, solPrice));
    return formatted;
  } catch (error) {
    console.error('❌ Failed to get formatted trending creators:', error);
    return [];
  }
};

